import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useExperimentEnabled } from '@/experiment/experiments-client';
import { useApplications } from '@/shared/hooks/useApplications';
import { dropboxAppRow } from '../app-rows/dropboxAppRow';
import { gmailAppRow } from '../app-rows/gmailAppRow';
import { googleAppRow } from '../app-rows/googleAppRow';
import { googleDocsAppRow } from '../app-rows/googleDocsAppRow';
import { greenhouseAppRow } from '../app-rows/greenhouseAppRow';
import { hubspotAppRow } from '../app-rows/hubspotAppRow';
import { jiraAppRow } from '../app-rows/jiraAppRow';
import { microsoftAppRow } from '../app-rows/microsoftAppRow';
import { notionAppRow } from '../app-rows/notionAppRow';
import { otterAPIAppRow } from '../app-rows/otterAPIAppRow';
import { salesforceAppRow } from '../app-rows/salesforceAppRow';
import { slackAppRow } from '../app-rows/slackAppRow';
import { workatoAppRows } from '../app-rows/workatoAppRows';
import { zapierAppRow } from '../app-rows/zapierAppRow';
import { zoomAppRow } from '../app-rows/zoomAppRow';
import { workatoAppsConfigQueryCreator } from '../query/workatoAppsConfigQueryCreator';
import type { AppRow } from '../type/AppRow';
import { useWorkatoApps } from './useWorkatoApps';

/**
 * Get all the connected apps with meta-data and settings component
 */
export function useAppsSettings(): { appRows: AppRow[]; isLoading: boolean } {
  const t = useTranslations('settings:integrations');
  const zapierEnabled = useExperimentEnabled('zapier_fe_integration');
  const isPublicApiEnabled = useExperimentEnabled('public_api');

  const { workatoApps, isLoading: isWorkatoAppsLoading } = useWorkatoApps();
  const { data: workatoConfig, isLoading: isWorkatoConfigLoading } = useQuery(
    workatoAppsConfigQueryCreator(),
  );

  const { data: applicationsData, isLoading: isApplicationsLoading } =
    useApplications({ includeStandard: false });

  const isLoading =
    isWorkatoAppsLoading || isWorkatoConfigLoading || isApplicationsLoading;

  const availableAppNames = new Set(
    applicationsData?.applications?.map((app) => app.name) ?? [],
  );

  const appRows: AppRow[] = [
    ...workatoAppRows(workatoApps ?? [], workatoConfig?.items ?? [], t),
    dropboxAppRow,
    googleAppRow,
    hubspotAppRow,
    microsoftAppRow,
    salesforceAppRow,
    slackAppRow,
    zoomAppRow,
  ];

  if (availableAppNames.has('google-drive')) {
    appRows.push(googleDocsAppRow);
  }

  if (availableAppNames.has('google-mcp')) {
    appRows.push(gmailAppRow);
  }

  if (zapierEnabled) {
    appRows.push(zapierAppRow);
  }

  if (isPublicApiEnabled) {
    appRows.push(otterAPIAppRow);
  }

  if (availableAppNames.has('jira-mcp')) {
    appRows.push(jiraAppRow);
  }

  if (availableAppNames.has('notion-mcp')) {
    appRows.push(notionAppRow);
  }

  if (availableAppNames.has('greenhouse-mcp')) {
    appRows.push(greenhouseAppRow);
  }

  return { appRows, isLoading };
}
